import { Icon } from '@ps/isp-web-component-library';
import { ChevronSmallDown } from '@ps/isp-web-component-library/icons/ChevronSmallDown';
import { Info } from '@ps/isp-web-component-library/icons/Info';
import type { Size } from '@ps/web-core-enums';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import {
  TrackingCategory,
  TrackingContext,
  type TrackingContextValue,
  bonus,
  useTrackers,
  widget
} from '@ps/web-shared-analytics';
import { Translation, usePrevious, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { MouseEvent } from 'react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { StarsWebExtraWallet } from '@swf/core/models/sports/extraWallets/types';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';

import useOnNavigate from '../../hooks/useOnNavigate';
import UpdateCurrencyButton from '../UpdateCurrencyButton/UpdateCurrencyButton';

import { TXTWCL_You_have_promotions_available__ELL } from './ExtraWalletsInfoBar.messages.json';
import styles from './ExtraWalletsInfoBar.scss';
import ExtraWalletsTitle from './components/ExtraWalletsTitle/ExtraWalletsTitle';
import WalletDetail from './components/WalletDetail/WalletDetail';

const appendTrackingEntities = (
  trackingContext: TrackingContextValue,
  bonusDetail: StarsWebExtraWallet
): TrackingContextValue =>
  mergeWithTrackingContextValue(trackingContext, [
    bonus({
      currency: bonusDetail.currency,
      amount: bonusDetail.amount,
      awardingType: bonusDetail.awardingType,
      id: bonusDetail.id,
      type: bonusDetail.type,
      expiry: new Date(bonusDetail.expiry).getTime()
    })
  ]);

const InfoBar: React.FC = () => {
  const allWallets = useSelector(select.extraWallets.getAllEnabledWallets);
  const walletsByAccountCurrency = useSelector(select.extraWallets.getWalletsByAccountCurrency);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [walletDetailsOpen, setWalletDetailsOpen] = useState(false);
  const [walletIdOpen, setWalletIdOpen] = useState('');
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const dispatch = useDispatch<Dispatch>();
  const isBetSlipOpen = useSelector(select.betSlip.isOpen);
  const previousBetSlipOpen = usePrevious(isBetSlipOpen);
  const trackingContextValue = useContext(TrackingContext);
  const { buttonPressed, widgetViewed } = useTrackers();
  const widgetTracked = useRef(false);

  useOnNavigate(() => setDetailsOpen(false));

  useEffect(() => {
    if (allWallets.length && !widgetTracked.current) {
      widgetViewed('Active Offers Bar');
      widgetTracked.current = true;
    }
  }, [allWallets.length, widgetViewed]);

  useEffect(() => {
    if (isClientAuthenticated) {
      dispatch.extraWallets.walletsRequested();
    }
  }, [isClientAuthenticated, dispatch.extraWallets]);

  useEffect(() => {
    if (previousBetSlipOpen && !isBetSlipOpen) {
      setDetailsOpen(false);
    }
  }, [isBetSlipOpen, previousBetSlipOpen]);

  if (allWallets.length === 0) {
    return null;
  }

  const hasPromotionInAnotherCurrency = walletsByAccountCurrency.length !== allWallets.length;

  const toggleDetails = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDetailsOpen(!detailsOpen);
    setWalletDetailsOpen(false);
    buttonPressed({ buttonName: detailsOpen ? 'Collapse' : 'Expand' });
  };

  const toggleWalletDetails = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.preventDefault();
    setWalletIdOpen(id);

    const isWalletAlreadyOpen = id === walletIdOpen;

    if (isWalletAlreadyOpen) {
      setWalletDetailsOpen(!walletDetailsOpen);
    } else {
      setWalletDetailsOpen(true);
    }
  };

  return (
    <details open={detailsOpen}>
      <summary className={styles.summary} onClick={toggleDetails}>
        <div className={styles.summaryContainer}>
          <ExtraWalletsTitle />
          <Icon icon={ChevronSmallDown} className={classNames(styles.summaryIcon)} size="x1" />
        </div>
      </summary>
      <div className={classNames(styles.body)}>
        {allWallets.map((wallet) => (
          <TrackingContext.Provider
            key={wallet.id}
            value={appendTrackingEntities(trackingContextValue, wallet)}
          >
            <WalletDetail
              wallet={wallet}
              hasVisibleDetails={walletIdOpen === wallet.id && walletDetailsOpen}
              toggleDetails={toggleWalletDetails}
            />
          </TrackingContext.Provider>
        ))}

        {hasPromotionInAnotherCurrency && (
          <>
            <div className={styles.promotionsMessage}>
              <Icon icon={Info} size="x1" />
              <span>
                <Translation {...TXTWCL_You_have_promotions_available__ELL} />
              </span>
            </div>
            <UpdateCurrencyButton variant="brand-promotion" isFullWidth />
          </>
        )}
      </div>
    </details>
  );
};

interface Props {
  hiddenFrom?: Size;
}

export const ExtraWalletsInfoBar: React.FC<Props> = ({ hiddenFrom }) => {
  const isBonusCashEnabled = useFeature(FeatureFlag.ISPBonusCash);
  const isFreeBetsEnabled = useFeature(FeatureFlag.ISPSportsFreeBets);

  const promotionsEnabled = isBonusCashEnabled || isFreeBetsEnabled;

  const { greaterThan } = useScreenSize();

  const isHidden = hiddenFrom ? greaterThan(hiddenFrom) : false;

  if (!promotionsEnabled || isHidden) {
    return null;
  }

  return (
    <TrackingContext.Provider
      value={{
        category: TrackingCategory.Sports,
        entities: [widget({ widgetId: 'activeOffersWidget', widgetName: 'Active Offers Bar' })]
      }}
    >
      <InfoBar />
    </TrackingContext.Provider>
  );
};
